import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { ChevronLeftIcon, EyeIcon, EyeSlashIcon, PhoneIcon, LockClosedIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

export default function Login() {
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' or 'email'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    const identifier = loginMethod === 'phone' ? phoneNumber : email;
    if (!identifier || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const result = await login(identifier, password, rememberPassword);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/home');
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      toast.error(error.message || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Top Section with Coral Background */}
      <div className="bg-[#F47B6C] p-6 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeftIcon className="h-6 w-6 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">Bounty Game</h1>
          <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5">
            <img src="https://flagcdn.com/w20/us.png" alt="EN" className="w-5 h-5 rounded-full" />
            <span className="text-lg font-medium text-white">EN</span>
          </div>
        </div>

        {/* Title */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold mb-2 text-white">Log in</h1>
          <p className="text-white/80 text-sm">Please log in with your phone number or email</p>
          <p className="text-white/80 text-sm">If you forget your password, please contact customer service</p>
        </div>
      </div>

      {/* White Background Content Section */}
      <div className="bg-white -mt-6 rounded-t-[2rem] p-6 min-h-[calc(100vh-16rem)] shadow-sm">
        {/* Login Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            className={`flex-1 pb-2 text-lg ${loginMethod === 'phone' ? 'text-[#F47B6C] border-b-2 border-[#F47B6C]' : 'text-gray-400'}`}
            onClick={() => setLoginMethod('phone')}
          >
            <div className="flex items-center justify-center gap-2">
              <PhoneIcon className="h-5 w-5" />
              <span>phone number</span>
            </div>
          </button>
          <button
            className={`flex-1 pb-2 text-lg ${loginMethod === 'email' ? 'text-[#F47B6C] border-b-2 border-[#F47B6C]' : 'text-gray-400'}`}
            onClick={() => setLoginMethod('email')}
          >
            <div className="flex items-center justify-center gap-2">
              <EnvelopeIcon className="h-5 w-5" />
              <span>Email Login</span>
            </div>
          </button>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          {/* Phone/Email Input */}
          {loginMethod === 'phone' ? (
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 text-[#F47B6C]" />
                <span className="text-base text-gray-700">Phone number</span>
              </label>
              <div className="flex gap-2">
                <select className="bg-gray-50 rounded-xl px-4 py-3 w-24 text-gray-700 border border-gray-200">
                  <option>+91</option>
                </select>
                <input
                  type="tel"
                  placeholder="Please enter the phone number"
                  className="bg-gray-50 rounded-xl px-4 py-3 w-full text-gray-700 placeholder-gray-400 border border-gray-200"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-[#F47B6C]" />
                <span className="text-base text-gray-700">Email</span>
              </label>
              <input
                type="email"
                placeholder="Please enter your email"
                className="bg-gray-50 rounded-xl px-4 py-3 w-full text-gray-700 placeholder-gray-400 border border-gray-200"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          )}

          {/* Password Input */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <LockClosedIcon className="h-5 w-5 text-[#F47B6C]" />
              <span className="text-base text-gray-700">Password</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="bg-gray-50 rounded-xl px-4 py-3 w-full text-gray-700 placeholder-gray-400 border border-gray-200"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Remember Password */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="remember"
              className="h-5 w-5 rounded border-gray-300 text-[#F47B6C] focus:ring-[#F47B6C]"
              checked={rememberPassword}
              onChange={(e) => setRememberPassword(e.target.checked)}
            />
            <label htmlFor="remember" className="text-gray-500 text-sm">
              Remember password
            </label>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            className="w-full bg-gray-200 text-gray-600 rounded-full py-3.5 text-base font-medium mt-6"
          >
            Log in
          </button>

          {/* Register Link */}
          <Link
            to="/register"
            className="block w-full text-center border border-[#F47B6C] text-[#F47B6C] rounded-full py-3.5 text-base font-medium bg-white mt-4"
          >
            Register
          </Link>

          {/* Footer Links */}
          <div className="flex justify-between items-center mt-16">
            <Link to="/forgot-password" className="flex flex-col items-center w-1/2">
              <div className="bg-[#F47B6C]/10 rounded-lg p-3 mb-2">
                <LockClosedIcon className="h-6 w-6 text-[#F47B6C]" />
              </div>
              <span className="text-base text-gray-700">Forgot password</span>
            </Link>
            <Link to="/help" className="flex flex-col items-center w-1/2">
              <div className="bg-[#F47B6C]/10 rounded-lg p-3 mb-2">
                <img 
                  src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJjdXJyZW50Q29sb3IiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1tZXNzYWdlLWNpcmNsZSI+PHBhdGggZD0iTTIxIDExLjVhOC4zOCA4LjM4IDAgMCAxLS45IDMuOCA4LjUgOC41IDAgMCAxLTcuNiA0LjcgOC4zOCA4LjM4IDAgMCAxLTMuOC0uOUwzIDIxbDEuOS01LjdhOC4zOCA4LjM4IDAgMCAxLS45LTMuOCA4LjUgOC41IDAgMCAxIDQuNy03LjYgOC4zOCA4LjM4IDAgMCAxIDMuOC0uOWguNWE4LjQ4IDguNDggMCAwIDEgOCA4di41WiIvPjwvc3ZnPg==" 
                  alt="Customer Service"
                  className="h-6 w-6 text-[#F47B6C]"
                  style={{ filter: 'invert(56%) sepia(14%) saturate(2700%) hue-rotate(314deg) brightness(96%) contrast(95%)' }}
                />
              </div>
              <span className="text-base text-gray-700">Customer Service</span>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
} 